/* story-bible-edit — StoryBibleDialog
 * Full-screen editable view of the project's Story Bible.
 * Feb-2026: AI Auto-Populate — if the bible is empty when the dialog opens,
 * auto-triggers the story-bible autofill endpoint in the background.
 * Feb-2026: Persistent Unapplied Entries — user edits are stored as "drafts"
 * in the DB (separate from committed values). Apply drafts to add to change_cue.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollText, Check, Loader2, Sparkles } from 'lucide-react';
import { DraftableField } from '@/components/ui/DraftableField';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Small inline saved indicator (used by ThemesField)
function SavedBadge({ state }) {
  if (state === 'idle') return null;
  return (
    <span
      className={cn(
        'ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-opacity',
        state === 'saving' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' : '',
        state === 'saved'  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' : '',
        state === 'error'  ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' : '',
      )}
    >
      {state === 'saving' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
      {state === 'saved'  && <Check className="h-2.5 w-2.5" />}
      {state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : 'Error'}
    </span>
  );
}

// Themes is a string[] — edit as comma-separated, render as chips on blur
function ThemesField({ value = [], onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value.join(', '));
  const [themes, setThemes]   = useState(value);
  const [status, setStatus]   = useState('idle');
  let timer = React.useRef(null);

  useEffect(() => {
    setThemes(value);
    if (!editing) setDraft(value.join(', '));
  }, [value]);  // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback(async () => {
    setEditing(false);
    const next = draft.split(',').map((s) => s.trim()).filter(Boolean);
    setThemes(next);
    const prev = value.join(', ');
    if (next.join(', ') === prev) return;
    setStatus('saving');
    try {
      await onSave('themes', next);
      setStatus('saved');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setStatus('idle'), 1800);
    } catch {
      setStatus('error');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setStatus('idle'), 2500);
    }
  }, [draft, value, onSave]);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
        Themes
        <SavedBadge state={status} />
      </div>
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          placeholder="Romance, Redemption, Power — comma separated"
          data-testid="story-bible-edit-themes"
          className="h-9 text-[13px]"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="story-bible-themes-edit-trigger"
          className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-left hover:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          title="Click to edit themes"
        >
          {themes.length > 0 ? (
            themes.map((t) => (
              <Badge key={t} variant="secondary" className="h-5 px-2 text-[11.5px] font-normal">{t}</Badge>
            ))
          ) : (
            <span className="text-[12px] text-muted-foreground">Click to add themes…</span>
          )}
        </button>
      )}
    </div>
  );
}

export default function StoryBibleDialog({ open, onOpenChange, bible, projectTitle, projectId, onFieldSave, onBibleUpdate }) {
  const themes = Array.isArray(bible?.themes) ? bible.themes : [];
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [drafts, setDrafts] = useState({});

  // Load existing drafts when dialog opens
  useEffect(() => {
    if (!open || !projectId) return;
    axios.get(`${API}/projects/${projectId}/bible-drafts`, { withCredentials: true })
      .then((r) => setDrafts(r.data.drafts || {}))
      .catch(() => {});
  }, [open, projectId]);

  // AI Auto-Populate: when the dialog opens and the bible is mostly empty,
  // kick off a background autofill automatically (once per session per project).
  useEffect(() => {
    if (!open || !projectId) return;
    const sessionKey = `loom:story-bible-autofill:${projectId}`;
    if (sessionStorage.getItem(sessionKey)) return;
    const hasData = bible && (bible.story_arc || bible.mood || bible.worldbuilding ||
      (Array.isArray(bible.themes) && bible.themes.length > 0));
    if (hasData) return;
    sessionStorage.setItem(sessionKey, '1');
    setAutoPopulating(true);
    const run = async () => {
      try {
        await axios.post(`${API}/projects/${projectId}/story-bible/autofill`, {}, { withCredentials: true });
        const deadline = Date.now() + 8 * 60 * 1000;
        for (;;) {
          await new Promise((r) => setTimeout(r, 5000));
          const s = await axios.get(`${API}/projects/${projectId}/story-bible/autofill/status`, { withCredentials: true });
          const job = s.data.job;
          if (!job || job.status === 'running') {
            if (Date.now() > deadline) break;
            continue;
          }
          if (job.status === 'error') break;
          const p = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
          if (onBibleUpdate) onBibleUpdate(p.data?.story_bible);
          toast.success('Story Bible filled from manuscript.', {
            description: `${job.chapters_read} chapters read`,
          });
          break;
        }
      } catch { /* soft fail */ }
      finally { setAutoPopulating(false); }
    };
    run();
  }, [open, projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async (key, value) => {
    if (!onFieldSave) return;
    await onFieldSave(key, value);
  }, [onFieldSave]);

  const handleDraftSave = useCallback(async (fieldKey, fieldLabel, draftValue) => {
    if (!projectId) return;
    try {
      const r = await axios.put(`${API}/projects/${projectId}/bible-drafts`,
        { field_key: fieldKey, draft_value: draftValue, field_label: fieldLabel },
        { withCredentials: true },
      );
      setDrafts(r.data.drafts || {});
    } catch { /* soft fail */ }
  }, [projectId]);

  const handleApply = useCallback(async (fieldKey) => {
    if (!projectId) return;
    try {
      const r = await axios.post(`${API}/projects/${projectId}/bible-drafts/${fieldKey}/apply`, {}, { withCredentials: true });
      setDrafts(r.data.drafts || {});
      if (onFieldSave) await onFieldSave(fieldKey, r.data.applied_value);
      toast.success('Change added to the build cue.', { description: 'It will be applied on your next Commit & Rebuild.' });
    } catch { toast.error('Could not apply the change.'); }
  }, [projectId, onFieldSave]);

  const handleDiscard = useCallback(async (fieldKey) => {
    if (!projectId) return;
    try {
      const r = await axios.delete(`${API}/projects/${projectId}/bible-drafts/${fieldKey}`, { withCredentials: true });
      setDrafts(r.data.drafts || {});
    } catch { /* soft fail */ }
  }, [projectId]);

  if (!bible && !autoPopulating) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] w-[92vw] max-w-3xl overflow-auto p-0"
        data-testid="story-bible-dialog"
      >
        <DialogHeader className="border-b border-panel-border px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-bold">
            <ScrollText className="h-4 w-4 text-primary" />
            Story Bible
            {projectTitle && (
              <span className="text-[12px] font-normal text-muted-foreground">· {projectTitle}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Edit any field — changes save on blur. Use Apply to queue a change for the next Commit &amp; Rebuild.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="story-bible-dialog-scroll"
          className="space-y-5 overflow-y-auto px-5 py-4 pb-8 text-[13px]"
        >
          {autoPopulating && (
            <div className="flex items-center gap-2.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5 text-[12px] text-primary">
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
              <span>AI is reading your manuscript and filling the Bible… This takes 1–2 minutes.</span>
            </div>
          )}
          {!bible && autoPopulating ? (
            <div className="flex h-32 items-center justify-center text-[13px] text-muted-foreground">
              <Sparkles className="h-4 w-4 mr-2 animate-pulse text-primary" />
              Preparing Story Bible…
            </div>
          ) : (
            <>
              <DraftableField
                label="Story Arc"
                committedValue={bible?.story_arc}
                draftValue={drafts['story_arc']}
                rows={5}
                onDraftSave={(v) => { handleSave('story_arc', v); handleDraftSave('story_arc', 'Story Arc', v); }}
                onApply={() => handleApply('story_arc')}
                onDiscard={() => handleDiscard('story_arc')}
                testId="story-bible-edit-story_arc"
              />
              <DraftableField
                label="Mood"
                committedValue={bible?.mood}
                draftValue={drafts['mood']}
                multiline={false}
                onDraftSave={(v) => { handleSave('mood', v); handleDraftSave('mood', 'Mood', v); }}
                onApply={() => handleApply('mood')}
                onDiscard={() => handleDiscard('mood')}
                testId="story-bible-edit-mood"
              />
              <ThemesField value={themes} onSave={handleSave} />
              <DraftableField
                label="Worldbuilding"
                committedValue={bible?.worldbuilding}
                draftValue={drafts['worldbuilding']}
                rows={4}
                onDraftSave={(v) => { handleSave('worldbuilding', v); handleDraftSave('worldbuilding', 'Worldbuilding', v); }}
                onApply={() => handleApply('worldbuilding')}
                onDiscard={() => handleDiscard('worldbuilding')}
                testId="story-bible-edit-worldbuilding"
              />

              {bible?.imported_at && (
                <div className="pt-1 text-[11px] text-muted-foreground">
                  Imported {new Date(bible.imported_at).toLocaleString()}
                  {bible.word_count ? ` · ${bible.word_count.toLocaleString()} words` : ''}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
